'use client'

import { useEffect, useRef } from 'react'

interface UseVisibleIntervalOptions {
  /**
   * Interval duration in milliseconds
   * @default 60000 (1 minute)
   */
  interval?: number
  /**
   * Whether the hook should be active based on dependencies
   * If false, no interval will be started and the function won't run
   */
  enabled?: boolean
}

/**
 * Hook that runs a function at regular intervals when the page is visible,
 * with automatic pause/resume behavior on visibility/focus changes.
 */
export default function useVisibleInterval(
  callback: () => void | Promise<void>,
  options: UseVisibleIntervalOptions = {}
) {
  const { interval = 60000, enabled = true } = options

  const intervalRef = useRef<number | null>(null)
  const wiredRef = useRef(false)
  const callbackRef = useRef(callback)
  const lastExecutionRef = useRef<number>(0)

  // Always keep the ref updated with the latest callback
  callbackRef.current = callback

  function startInterval() {
    if (intervalRef.current != null) {
      return
    }
    intervalRef.current = window.setInterval(() => {
      callbackRef.current()
    }, interval)
  }

  function stopInterval() {
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  // Execute the callback immediately (with debouncing to prevent rapid successive calls)
  function executeNow() {
    const now = Date.now()
    const timeSinceLastExecution = now - lastExecutionRef.current

    // Prevent rapid successive executions (within 250ms)
    if (timeSinceLastExecution < 250) {
      return
    }

    lastExecutionRef.current = now
    void callbackRef.current()
  }

  useEffect(() => {
    if (!enabled) {
      // Reset when disabled so it can be re-enabled later
      wiredRef.current = false
      lastExecutionRef.current = 0
      return
    }

    if (wiredRef.current) {
      return
    }
    wiredRef.current = true

    // Execute immediately on mount/enable
    executeNow()

    // Start interval if page is currently visible
    if (document.visibilityState === 'visible') {
      startInterval()
    }

    // Handle visibility changes
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        executeNow()
        startInterval()
      } else {
        stopInterval()
      }
    }

    // Handle window focus
    const onFocus = () => {
      executeNow()
      startInterval()
    }

    // Handle page show (e.g., iOS bfcache restore)
    const onPageShow = () => {
      executeNow()
      startInterval()
    }

    // Add event listeners
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    window.addEventListener('pageshow', onPageShow)

    // Cleanup function - called when effect re-runs or component unmounts
    return () => {
      stopInterval()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('pageshow', onPageShow)
      wiredRef.current = false
      lastExecutionRef.current = 0
    }
  }, [enabled, interval])

  return null
}
